import { skipToken } from '@reduxjs/toolkit/query';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import useAuth from 'hooks/useAuth';
import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useGetTaskByIdQuery, useUpdateTaskMutation } from 'store/api/tasks';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { Task, TaskForm } from 'types/tasks';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import {
    TASK_FORM_CLASSIFIERS,
    TASK_SECTION_FIELDS,
    TaskFormContext,
    TaskSectionId,
    taskDtoToForm,
    taskSectionToRequest,
} from '../taskFormMapping';
import { AssigneesFields } from './AssigneesFields';
import { ConnectionsFields } from './ConnectionsFields';
import { DeadlineCalendarFields, TaskEventFields } from './DeadlineCalendarFields';
import { DmsFields } from './DmsFields';
import { GeneralFields, OfficialFields } from './GeneralFields';
import { NotesFields } from './NotesFields';
import { eventValidationErrorId, TaskFormState, useTaskForm } from './useTaskForm';

const SECTION_FIELDS: Record<TaskSectionId, (form: TaskFormState) => ReactNode> = {
    general: (form) => (
        <>
            <GeneralFields form={form} />
            <OfficialFields form={form} />
        </>
    ),
    assignees: (form) => <AssigneesFields form={form} />,
    connections: (form) => <ConnectionsFields form={form} />,
    'deadline-calendar': (form) => (
        <>
            <DeadlineCalendarFields form={form} />
            {form.calendarSync ? <TaskEventFields form={form} /> : null}
        </>
    ),
    dms: (form) => <DmsFields form={form} />,
    notes: (form) => <NotesFields form={form} />,
};

interface SectionFormProps {
    section: TaskSectionId;
    initialData: TaskForm;
    context: TaskFormContext;
    onClose: () => void;
}

function TaskSectionForm({ section, initialData, context, onClose }: SectionFormProps) {
    const form = useTaskForm({ type: 'edit', initialData, classifiers: context.classifiers });
    // The page's own query argument, so the refresh after the save is the page's.
    const { id: taskId } = useParams();
    const { refetch } = useGetTaskByIdQuery(taskId ?? skipToken);
    const [updateTask] = useUpdateTaskMutation();

    const onSave = async (values: TaskForm) => {
        const eventErrorId = section === 'deadline-calendar' ? eventValidationErrorId(form) : null;
        if (eventErrorId) {
            errorAlert(t(eventErrorId));
            return;
        }
        await saveSection(values, {
            section,
            sectionFields: TASK_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => taskDtoToForm(dto, context),
            // The organiser this form resolved for its own types and Official flag, which the event takes along.
            toRequest: (edited, latest) => taskSectionToRequest(edited, latest, values.taskEvent?.inviteSender),
            update: (request) => updateTask({ ...request, id: taskId }).unwrap(),
            successMessageId: 'tasks.alerts.task-updated',
            onSaved: onClose,
        });
    };

    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose} formKey="task-edit">
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: TaskSectionId;
    task: Task;
    onClose: () => void;
}

function TaskSectionEditor({ section, task, onClose }: EditorProps) {
    const { user: currentUser } = useAuth();
    const { data: tenantData, isLoading: isLoadingTenant } = useGetCurrentTenantQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: TASK_FORM_CLASSIFIERS });

    const loaded = useFrozenOnceLoaded(!!currentUser && !isLoadingTenant && !isLoadingClassifiers, () => {
        // `ready` holds only with a current user.
        const context = { classifiers, currentUser: currentUser!, emailOrganizer: tenantData?.emailOrganizer };
        return { initialData: taskDtoToForm(task, context), context };
    });

    if (!loaded) {
        return <Loader />;
    }
    return (
        <TaskSectionForm
            section={section}
            initialData={loaded.initialData}
            context={loaded.context}
            onClose={onClose}
        />
    );
}

interface TaskSectionProps {
    id: TaskSectionId;
    titleId: string;
    task: Task;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the card edits. */
    rows?: Row[];
}

/** A task card on the record page that edits in place with the task form's fields for that card. */
export function TaskSection({ id, titleId, task, children, rows }: TaskSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <TaskSectionEditor section={id} task={task} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
